/* eslint-disable max-len, no-nested-ternary */

import { Dispatch, FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Badge, Button, Card, Divider, Tabs, Modal, Typography } from 'antd';
import { RootState } from 'store';
import { CheckCircleFilled, CheckOutlined, CloseCircleFilled, CopyOutlined, PrinterOutlined, MinusCircleFilled } from '@ant-design/icons';
import { useReactToPrint } from 'react-to-print';
import { BsExclamationOctagon } from 'react-icons/bs';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import TimeReportProgress from 'modules/project/components/TimeReportProgress';
import Comments from 'components/Comments';
import ActivityLogs from 'components/ActivityLogs';
import { TIME_ENTRY_WS_EVENT, isLocalEcho, TimeEntryWsEvent } from 'services/timeTracking';
import useWebSocket from 'utils/useWebSocket';
import apiRequests from 'utils/api';
import apiRoutes from 'config/apiRoute';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import { UserResponse } from 'types';
import { FormBuilderData } from 'types/formBuilder';
import RequestForm, { RequestFormHandle, RequestFormProps } from '../RequestForm';
import { PrintData, PrintRequestForm, buildMarkdown, buildRichHtml } from '../RequestForm/components/PrintRequestForm';
import RequestTimelogTab from '../RequestTimelogTab';
import RequestTaskTab from '../RequestTaskTab';
import RequestFormHeader from '../RequestFormHeader';
import RequestFilesTab from '../RequestFilesTab';
import RequestMetaFields from './RequestMetaFields';
import style from '../RequestForm/index.module.less';
import styleNoDraft from './requestUpdateNoDraft.module.less';

const { Text } = Typography;

interface RequestUpdateNoDraftProps {
  request: Record<string, any>;
  formBuilder?: FormBuilderData;
  projectUuid?: string;
  pageUrl: string;
  wrapSize: 'xl' | 'xxl';
  highlightCommentUuid?: string;
  resetForm?: RequestFormProps['resetForm'];
  goBack?: boolean;
  noPadding?: boolean;
  showTimeReport?: boolean;
  hideTitle?: boolean;
  hideComments?: boolean;
  estimatedResetTrigger?: number;
  setRequest: Dispatch<any>;
  onSaveRequest: (values: any) => Promise<any>;
 onFetchRequest: () => Promise<void>;
 hideGoToProject?: boolean;
}

const RequestUpdateNoDraft: FC<RequestUpdateNoDraftProps> = ({
  request,
  formBuilder,
  projectUuid,
  pageUrl,
  wrapSize,
  highlightCommentUuid,
  resetForm,
  goBack,
  hideGoToProject,
  noPadding,
  showTimeReport,
  hideTitle,
  hideComments,
  estimatedResetTrigger,
  setRequest,
  onSaveRequest,
  onFetchRequest,
}) => {
  const user: UserResponse = useSelector((store: any) => store.auth.user);

  const [requestTasks, setRequestTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(hideComments ? 'overview' : 'comments');

  const [saving, setSaving] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<string | undefined>(request?.status);
  const [statusValidationFailed, setStatusValidationFailed] = useState<{ label: string; validate: boolean }[]>();

  const listForms = useSelector((state: RootState) => state.resources.requestForms);
  const requestFormRef = useRef<RequestFormHandle>(null);
  const breakpoint = useBreakpoint();
  const isMobile = !breakpoint.md;

  const printFormRef = useRef<HTMLDivElement>(null);

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState<{ client: string; owner: string; requestName: string; estimate: string; answers: Record<string, any> } | null>(null);
  const [copiedRich, setCopiedRich] = useState(false);
  const [copiedMd, setCopiedMd] = useState(false);

  const handlePrint = useReactToPrint({
    content: () => printFormRef.current,
    documentTitle: previewData?.requestName,
  });
  const isFreelancer = user.contact.type === 'freelancer';
  const isRetainer = request.project.is_retainer;

  const onValidateStatusComplete = () => {
    if (!request) return false;

    let tasksCompleted = true;
    let tasksAssignees = true;
    let tasksTimelogs = true;

    const validateTasks = (tasks: any[]) => {
      tasks.forEach((task) => {
        if (task.task_status !== 'cancelled' && task.task_status !== 'seed') {

          if (task.task_status !== 'completed') {
            tasksCompleted = false;
          }

          const hasResponsible = task.related_users?.some(
            (u: any) => u.pivot?.type === 'responsible'
          );
          if (!hasResponsible) {
            tasksAssignees = false;
          }

          if (Number(task.total_timelogs) === 0) {
            tasksTimelogs = false;
          }
        }

        if (task.subtasks && task.subtasks.length > 0) {
          validateTasks(task.subtasks);
        }
      });
    };

    validateTasks(requestTasks);

    const hasResponsible = request.related_users?.some((u: any) => u.pivot.type === 'responsible');

    const list = [
      { label: 'Time spent filled in', validate: Number(request.time_spent) > 0 },
      { label: 'Time spent less than maximum estimated time', validate: Number(request.time_spent) <= request.estimated_max },
      { label: 'All related tasks completed', validate: tasksCompleted },
      { label: 'Responsible person filled in', validate: hasResponsible },
      { label: 'All tasks have assignees', validate: tasksAssignees },
      { label: 'All tasks have timelogs', validate: tasksTimelogs },
    ];

    return list.find((item) => !item.validate) ? list : true;
  };

  const onUpdateStatus = async (newStatus: string, force = false) => {
    if (newStatus === 'completed' && !force && request) {
      const validate = onValidateStatusComplete();

      if (validate !== true) {
        if (typeof validate === 'object') {
          setStatusValidationFailed(validate);
        }
        return;
      }
    }

    setCurrentStatus(newStatus);

    setSaving(true);

    try {
      await onSaveRequest({ status: newStatus });
    } catch (error) {
      asyncErrorHandler(error);
    } finally {
      setSaving(false);
    }
  };

  const onUpdateBillable = async (billable: boolean) => {
    setSaving(true);

    try {
      await onSaveRequest({ billable });
      setRequest((prev: any) => ({ ...prev, billable }));
    } catch (error) {
      asyncErrorHandler(error);
    } finally {
      setSaving(false);
    }
  };

  const onUpdateHandler = async (values: any) => {
    setSaving(true);

    try {
      return await onSaveRequest(values);
    } finally {
      setSaving(false);
    }
  };

  const onAddedTimelog = (timelog: any) => {
    setRequest({ ...request, time_spent: (Number(request.time_spent) + timelog.minutes).toString() });
  };

  const fetchRequestTasks = async () => {
    if (!request?.uuid) return;
    setTasksLoading(true);
    try {
      const response = await apiRequests.get(`${apiRoutes.PROJECT_TASKS_TREE}`, {
        'filters[requests.uuid][]': request.uuid,
        with: 'total_timelogs',
      });
      setRequestTasks(response.data.data);
    } finally {
      setTasksLoading(false);
    }
  };

  const globalTask = useSelector((store: RootState) => store.globalTaskModal);
  const globalLogTrigger = useSelector((globalState: RootState) => globalState.globalLog.trigger);

  useEffect(() => {
    fetchRequestTasks();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.uuid, globalLogTrigger, globalTask.savedTrigger, globalTask.removedRequestTrigger, globalTask.updatedSubtaskTrigger]);

  useEffect(() => {
    if (resetForm && resetForm.oldRequest.status === currentStatus && currentStatus !== request.status) {
      setCurrentStatus(request.status);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetForm, request.status]);

  useWebSocket({
    channelName: 'model.changes',
    listen: {
      event: TIME_ENTRY_WS_EVENT,
      callback: (event: TimeEntryWsEvent) => {
        if (event.payload.project_uuid !== request.project.uuid) return;

        const isOptimisticallyHandled =
          isLocalEcho(event) && event.payload.source === 'request' && event.action === 'completed';
        if (isOptimisticallyHandled) return;

        if (event.action === 'completed' || event.action === 'updated' || event.action === 'deleted') {
          onFetchRequest();
        }
      },
    },
  });

  useWebSocket({
    channelName: `project.${request.project.uuid}`,
    listen: {
      event: '.project.budget',
      callback: (event: any) => {
        setRequest({
          ...request,
          project: {
            ...request.project,
            budget: event.budget,
            time_report: event.time_report,
          },
        });
      },
    },
  });

  const taskCount = useMemo(() => {
    const statusToIgnore = ['completed', 'cancelled'];

    const flattenTasks = (tasks: any[]): any[] => {
      return tasks.reduce((acc, task) => {
        return [...acc, task, ...flattenTasks(task.subtasks || [])];
      }, []);
    };

    const allTasksFlat = flattenTasks(requestTasks);

    const activeTasks = allTasksFlat.filter(
      (task) => !statusToIgnore.includes(task.task_status)
    );

    return activeTasks.length;
  }, [requestTasks]);

  const ActionButtons = () => (
    <>
      <Button
        size="small"
        icon={copiedRich ? <CheckOutlined style={{ color: 'green' }} /> : <CopyOutlined />}
        onClick={() => {
          const html = buildRichHtml(previewData as PrintData, listForms);
          const blob = new Blob([html], { type: 'text/html' });
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (navigator.clipboard as any).write([new (window as any).ClipboardItem({ 'text/html': blob })]).then(() => {
            setCopiedRich(true);
            setTimeout(() => setCopiedRich(false), 2000);
          }).catch(() => {
            navigator.clipboard.writeText(buildMarkdown(previewData as PrintData, listForms)).then(() => {
              setCopiedRich(true);
              setTimeout(() => setCopiedRich(false), 2000);
            });
          });
        }}
      >
        Copy
      </Button>

      <Button
        size="small"
        icon={copiedMd ? <CheckOutlined style={{ color: 'green' }} /> : <CopyOutlined />}
        onClick={() => {
          navigator.clipboard.writeText(buildMarkdown(previewData as PrintData, listForms));
          setCopiedMd(true);
          setTimeout(() => setCopiedMd(false), 2000);
        }}
      >
        .MD
      </Button>

      <Button
        size="small"
        icon={<PrinterOutlined />}
        onClick={handlePrint}
      >
        Print
      </Button>
    </>
  );

  const timeReport = <TimeReportProgress budget={request.project.budget} report={request.project.time_report} />;

  const requestForm = (
    <RequestForm
      ref={requestFormRef}
      type="view"
      className={wrapSize === 'xl' ? 'xl-h-full' : 'xxl-h-full'}
      currentStatus={currentStatus}
      request={request}
      formBuilder={formBuilder}
      projectUuid={projectUuid}
      pageUrl={pageUrl}
      classNameColFormQuestions={`${style.colFormQuestions} transparent-scrollbar`}
      resetForm={resetForm}
      estimatedResetTrigger={estimatedResetTrigger}
      goBack={goBack}
      hideMetaRows
      onUpdate={onUpdateHandler}
      onAddedTimelog={onAddedTimelog}
      onChangeStatus={onUpdateStatus}
      onOpenPreview={(data) => {
        setPreviewData(data);
        setPreviewModalOpen(true);
      }}
      disableFormAnswers
    />
  );

  const tabs = (
    <Tabs className={`tab-fullscreen tab-secondary ${styleNoDraft.infoTabs}`} activeKey={activeTab} onChange={setActiveTab}>
      <Tabs.TabPane key="overview" tab="Overview" style={{ overflowY: 'auto', overflowX: 'hidden', paddingRight: 6 }}>
        {requestForm}
      </Tabs.TabPane>

      {!hideComments && (
        <Tabs.TabPane key="comments" tab="Comments" className={styleNoDraft.commentTab}>
          <Comments
            projectUuid={request.project?.uuid}
            type="requests"
            typeUuid={request.uuid}
            textEditorClassName="max-h-small"
            highlightUuid={highlightCommentUuid}
            replaceUserInTemplate={request.requester}
            mentionTypes={['regular_user', 'group', 'contact']}
            canComment={!isFreelancer}
            maxFullHeight
          />
        </Tabs.TabPane>
      )}

      <Tabs.TabPane
        key="tasks"
        tab={
          <Badge count={taskCount} className="ant-badge-color-dark-blue">
            Tasks
          </Badge>
        }
      >
        <RequestTaskTab
          request={request}
          tasks={requestTasks}
          tasksLoading={tasksLoading}
          fetchRequestTasks={fetchRequestTasks}
          pageUrl={pageUrl}
          showTimeReport={showTimeReport}
          setRequest={setRequest}
        />
      </Tabs.TabPane>

      <Tabs.TabPane key="logs" tab="Activity logs">
        <ActivityLogs model={request} modelType="request" style={{ height: 'calc(100% - 8px)', overflow: 'auto' }} />
      </Tabs.TabPane>

      <Tabs.TabPane key="timelogs" tab="Timelogs">
        <RequestTimelogTab uuid={request.uuid} projectUuid={request.project?.uuid} />
      </Tabs.TabPane>

      <Tabs.TabPane key="files" tab="Files">
        <RequestFilesTab uuid={request.uuid} isActive={activeTab === 'files'} />
      </Tabs.TabPane>
    </Tabs>
  );

  return (
    <Card
      className={wrapSize === 'xl' ? style.containerXl : style.containerXxl}
      bodyStyle={{
        padding: noPadding ? '0' : '8px',
      }}
      bordered={false}
    >
      <Modal
        visible={statusValidationFailed !== undefined}
        className="ant-modal-confirm"
        width={430}
        okText="Complete anyway"
        cancelText="Go back and edit"
        onOk={() => {
          setStatusValidationFailed(undefined);
          onUpdateStatus('completed', true);
        }}
        onCancel={() => {
          setStatusValidationFailed(undefined);
        }}
        destroyOnClose
      >
        <div className="ant-modal-confirm-body">
          <BsExclamationOctagon className="danger anticon" style={{ fontSize: 20 }} />

          <span className="ant-modal-confirm-title">Ooops, there is something wrong!</span>

          <div className="ant-modal-confirm-content">
            {statusValidationFailed?.map((item) => {
              const isTimeSpentValidation = item.label === 'Time spent less than maximum estimated time';
              const isNeutral = isTimeSpentValidation && request.estimated_max === 0;

              let icon;
              if (isNeutral) {
                icon = <MinusCircleFilled style={{ color: '#8c8c8c' }} />;
              } else if (item.validate) {
                icon = <CheckCircleFilled className="green" />;
              } else {
                icon = <CloseCircleFilled className="danger" />;
              }

              return (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: 6, fontSize: 18 }}>
                    {icon}
                  </span>
                  {item.label}
                </div>
              );
            })}
          </div>
        </div>
      </Modal>

      <Modal
        visible={previewModalOpen}
        onCancel={() => setPreviewModalOpen(false)}
        footer={null}
        title={null}
        closable
        width={700}
        destroyOnClose
        bodyStyle={{ padding: 0 }}
        getContainer={false}
        className={style.customModal}
      >
        {previewData && (
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>

              <div style={{ flex: 1, borderRadius: '8px', overflow: 'hidden', backgroundColor: 'white' }}>
                <div style={{ background: '#1f1f1f', padding: '16px 24px', display: 'flex', alignItems: 'start', flexDirection: 'column', justifyContent: 'space-between', overflow: 'hidden', gap: 16 }}>
                  <Text ellipsis style={{ color: '#fff', fontSize: 16, fontWeight: 600, paddingRight: 30 }}>
                    {previewData.requestName}
                  </Text>

                  {isMobile && (
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                      <ActionButtons />
                    </div>
                  )}
                </div>

                <div style={{ maxHeight: 620, overflowY: 'auto' }}>
                  <PrintRequestForm ref={printFormRef} data={previewData} />
                </div>
              </div>

              {!isMobile && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginLeft: 8 }}>
                  <ActionButtons />
                </div>
              )}
            </div>
          )}
      </Modal>

      <div className={styleNoDraft.containerBreakpoint}>
        <RequestFormHeader
          request={request}
          status={currentStatus}
          type="view"
          pageUrl={pageUrl}
          goBack={goBack}
          saving={saving}
          hideTitle={hideTitle}
          onChangeStatus={onUpdateStatus}
          onChangeBillable={onUpdateBillable}
        />

        <Divider type="horizontal" style={{ margin: '2px 0 0px' }} className="primary" />

        {isRetainer && timeReport}

        <RequestMetaFields
          request={request}
          projectUuid={projectUuid}
          isFreelancer={isFreelancer}
          resetForm={resetForm}
          estimatedResetTrigger={estimatedResetTrigger}
          onSaveRequest={onSaveRequest}
          onAddedTimelog={onAddedTimelog}
          onSavingChange={setSaving}
          hideGoToProject={hideGoToProject}
          setRequest={setRequest}
        />

        {tabs}
      </div>
    </Card>
  );
};

export default RequestUpdateNoDraft;
